import { useState, useEffect, useCallback } from 'react'
import { Wifi, RefreshCw, Search, ExternalLink, AlertTriangle, CheckCircle } from 'lucide-react'
import { clsx } from 'clsx'
import { formatDistanceToNow } from 'date-fns'
import { api } from '../lib/api'

const OUTAGE_STATUS: Record<string, { color: string; dot: string; label: string }> = {
  outage:   { color: 'text-rose-light', dot: 'bg-rose', label: 'Outage' },
  degraded: { color: 'text-amber-light', dot: 'bg-amber', label: 'Degraded' },
  ok:       { color: 'text-emerald-light', dot: 'bg-emerald', label: 'OK' },
}

function relativeTime(ts: string): string {
  if (!ts) return ''
  try { return formatDistanceToNow(new Date(ts), { addSuffix: false }) + ' ago' } catch { return ts }
}

export default function InternetHealthPage() {
  const [items, setItems] = useState<any[]>([])
  const [outages, setOutages] = useState<any[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [loading, setLoading] = useState(true)

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const [feedRes, outageRes] = await Promise.allSettled([
        api.get('/api/internet/feed', { params: { limit: 50 } }),
        api.get('/api/internet/outages'),
      ])
      if (feedRes.status === 'fulfilled') setItems(feedRes.value.data.items || [])
      if (outageRes.status === 'fulfilled') setOutages(outageRes.value.data.outages || [])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchData(); const i = setInterval(fetchData, 120_000); return () => clearInterval(i) }, [fetchData])

  const filtered = items.filter(item => {
    if (searchTerm) {
      const q = searchTerm.toLowerCase()
      if (
        !(item.title || '').toLowerCase().includes(q) &&
        !(item.summary || '').toLowerCase().includes(q)
      ) return false
    }
    return true
  })

  const activeOutages = outages.filter(o => o.status === 'outage' || o.status === 'degraded')

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text-primary flex items-center gap-3">
            <Wifi size={24} className="text-cyan" /> Internet Health Monitor
          </h1>
          <p className="text-sm text-text-secondary mt-1">Infrastructure signals from Cloudflare Radar, IODA, Downdetector, and ThousandEyes</p>
        </div>
        <button type="button" onClick={fetchData} className="btn-secondary text-xs">
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {activeOutages.length > 0 && (
        <div className="card p-5 border border-amber/30 bg-amber/5">
          <h2 className="text-sm font-semibold text-text-primary flex items-center gap-2 mb-3">
            <AlertTriangle size={16} className="text-amber" /> Active Outages &amp; Degradations
            <span className="badge-red text-[10px] ml-auto">{activeOutages.length} affected</span>
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {activeOutages.map((outage, idx) => {
              const status = OUTAGE_STATUS[outage.status] || OUTAGE_STATUS.degraded
              return (
                <div key={outage.country || idx} className="p-3 rounded-lg border border-border bg-surface-card">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={clsx('w-2 h-2 rounded-full', status.dot)} />
                    <span className="text-sm font-medium text-text-primary">{outage.country}</span>
                  </div>
                  <span className={clsx('text-xs', status.color)}>{status.label}</span>
                  {outage.details && <p className="text-[10px] text-text-muted mt-1">{outage.details}</p>}
                  {outage.timestamp && <p className="text-[10px] text-text-muted mt-0.5">{relativeTime(outage.timestamp)}</p>}
                </div>
              )
            })}
          </div>
        </div>
      )}

      {activeOutages.length === 0 && !loading && (
        <div className="card p-4 border border-emerald/30 bg-emerald/5">
          <div className="flex items-center gap-2">
            <CheckCircle size={16} className="text-emerald-light" />
            <span className="text-sm text-emerald-light font-medium">No active outages detected</span>
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
          <input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search internet health news..." className="input pl-9 py-2 text-xs" />
        </div>
      </div>

      {loading && !items.length && (
        <div className="flex items-center justify-center h-32">
          <RefreshCw size={20} className="animate-spin text-text-muted" />
          <span className="ml-2 text-text-muted text-sm">Fetching internet health data...</span>
        </div>
      )}

      <div className="space-y-3">
        {filtered.map((item, idx) => (
          <div key={item.id || idx} className="card card-hover p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-semibold text-text-primary leading-snug">
                  {item.url ? (
                    <a href={item.url} target="_blank" rel="noopener" className="hover:underline">{item.title}</a>
                  ) : item.title}
                </h3>
                <p className="text-xs text-text-secondary mt-1 leading-relaxed line-clamp-2">{item.summary}</p>
                <div className="flex items-center gap-2 mt-2 text-[10px] text-text-muted">
                  <span>{item.source}</span>
                  <span>&middot;</span>
                  <span>{relativeTime(item.timestamp)}</span>
                </div>
              </div>
              {item.url && (
                <a href={item.url} target="_blank" rel="noopener" className="text-text-muted hover:text-text-primary shrink-0">
                  <ExternalLink size={14} />
                </a>
              )}
            </div>
          </div>
        ))}
        {!loading && filtered.length === 0 && (
          <p className="text-xs text-text-muted text-center py-8">No items match your current filters</p>
        )}
      </div>
    </div>
  )
}
